/* eslint-disable react-hooks/exhaustive-deps */
import React, {useEffect, useReducer, useRef, useState} from "react"
import {get, head, isEmpty, uniq, trim} from "lodash"
import {useHistory, useLocation} from "react-router-dom"
import {v4 as uuid} from "uuid"
import cx from "classnames"
import {CreatedInfo} from "components/ui"
import {handleError, loadImage, toastError, toastSuccess} from "helpers"
import {
    DigitalDocumentFieldSetUp,
    TaskSubtype,
    UserType,
    DefaultDataType,
    TaskType,
    SignatureMethod,
    TaskStatus,
    DigitalTaskCondition
} from "types/tasks"
import {taskService} from "services"
import {DATA_TYPES, DEFAULT_DATA_TYPES, CHECKBOX_TYPES, INITIAL_TYPES, GRADE_DATA_TYPES} from "sections/Tasks/data"
import {DefinedUser, HeaderBody, FieldAction} from "./parts"
import styles from "./DigitalDocSetUp.module.css"
import {BaseLoading} from "components"
import * as queryString from "query-string"
import {duplicateField, getNextFieldIndex} from "./parts/utils/fields-duplication"
import {useTasks} from "sections/Tasks/hooks/useTasks"
import {useDigitalSetup} from "./useDigitalSetup"
import {useSubmit} from "./useSubmit"
import {useDigitalField} from "./useDigitalField"

function reducer(state, action) {
    return {...state, ...action}
}

export const SIGNATURE_METHOD_OPTIONS = [
    {id: SignatureMethod.HAND, name: "Sign by hand"},
    {id: SignatureMethod.KEYBOARD, name: "Sign by keyboard"},
    {id: SignatureMethod.BOTH, name: "Allow both signing methods"}
]

type Props = {
    taskData: any
    isCreation: boolean
    onCloseDigitalDocSetup: () => void
}

export function DigitalDocSetUp(props: Props) {
    const {taskData, isCreation, onCloseDigitalDocSetup} = props
    const [task, setTask] = useState<any>(null)
    const bodyRef = useRef(null)
    const wrapRef = useRef(null)
    const pageRef = useRef(null)
    const initialState = {
        fields: [],
        activeField: null,
        fieldBodyContent: null,
        isSaving: false,
        isImgsLoaded: false,
        definedUserTypes: [
            {id: UserType.STUDENT, name: "Student"},
            {id: UserType.STAFF, name: "Staff"},
            {id: UserType.OTHERS, name: "Others"}
        ],
        participants: [],
        definedUsers: [],
        combinedColor: {},
        scaleInfo: {},
        previousFieldInfo: {width: 32, height: 32, signatureTypes: []}
    }
    const [
        {
            fields,
            activeField,
            fieldBodyContent,
            isSaving,
            isImgsLoaded,
            definedUserTypes,
            definedUsers,
            participants,
            combinedColor,
            scaleInfo,
            previousFieldInfo
        },
        dispatch
    ] = useReducer(reducer, initialState)
    const history = useHistory()
    const location = useLocation()
    const queryParams = queryString.parse(location.search)
    const [errorFieldIds, setErrorFieldIds] = useState(null)
    const margin = 10
    const {getBackUrlTab} = useTasks()
    const {getIconByType} = useDigitalSetup()
    const {getDataSubmit} = useSubmit()
    const {initFields, getCircularDependencyFields} = useDigitalField()

    useEffect(() => {
        initData()
    }, [])

    const initData = async () => {
        let task = taskData
        try {
            if (taskData.type === TaskType.CUSTOM_DD) {
                task = await taskService.getCustomDigitalTask(taskData.id)
            } else {
                task = await taskService.getOne(taskData.id)
            }
            setTask(task)
            await Promise.all(task.digitalDocument.pages.map((image) => loadImage(image)))
        } catch (error) {
            console.log("Failed to load images", error)
        } finally {
            dispatch({isImgsLoaded: true})
        }
        const {allFields, newDefinedUsers, newParticipants, newDefinedUserTypes, scaleInfo} = initFields({
            task,
            definedUserTypes,
            pageRef,
            margin
        })
        dispatch({
            fields: allFields,
            definedUsers: newDefinedUsers,
            participants: newParticipants,
            definedUserTypes: newDefinedUserTypes,
            scaleInfo
        })
    }

    const onDragStart = () => {}

    const onDragStop = (_, data, newField) => {
        const {x: left, y: top} = data
        const newFields = fields.map((field) => {
            if (field.id === newField.id) {
                field.placeInformation = {...field.placeInformation, top, left}
            }
            return field
        })
        dispatch({fields: newFields})
    }

    const onResize = (_, {size}, newField) => {
        const newFields = fields.map((field) => {
            if (field.id === newField.id) {
                field.placeInformation = {...field.placeInformation, ...size}
            }
            return field
        })
        dispatch({fields: newFields, previousFieldInfo: {...previousFieldInfo, ...size}})
    }

    const setActiveField = (field) => {
        dispatch({activeField: field})
    }

    const onChangeField = (newField) => {
        const firstSignatureTypeId = get(newField, "signatureTypes[0].id", null)
        const newFields = fields.map((field) => {
            if (field.id === newField.id) {
                return {...field, ...newField}
            } else if (field.parentId === newField.id) {
                return {...field, parentSignatureTypeId: firstSignatureTypeId, signatureTypes: newField.signatureTypes}
            }
            return field
        })
        if (newField.signatureTypes && newField.signatureTypes.length > 0) {
            previousFieldInfo.signatureTypes = newField.signatureTypes
        }
        dispatch({fields: newFields, previousFieldInfo})
        return newFields
    }

    const onChangeFieldValue = (parentId, id, value) => {
        const newFields = fields.map((field) => {
            if (field.id === parentId) {
                field.fieldValues = field.fieldValues.map((fieldValue) => {
                    if (fieldValue.id === id) {
                        return {...fieldValue, ...value}
                    }
                    return fieldValue
                })
                return {...field}
            }
            return field
        })
        const fieldBodyContent = newFields.find((item) => item.id === parentId)
        dispatch({fields: newFields, fieldBodyContent})
    }

    const getSignatureTypes = (type) => {
        if (previousFieldInfo.signatureTypes && previousFieldInfo.signatureTypes.length) {
            return [DigitalDocumentFieldSetUp.SIGNATURE, DigitalDocumentFieldSetUp.INITIAL].includes(type)
                ? [head(previousFieldInfo.signatureTypes)]
                : previousFieldInfo.signatureTypes
        }
        const firstSigner = head(definedUsers)
        return firstSigner ? [firstSigner] : []
    }

    const onAddField = (type) => {
        const signatureTypes = getSignatureTypes(type)
        const nextFieldIndex = getNextFieldIndex(fields, type)
        const newField: any = {
            id: uuid(),
            type,
            label: "",
            uuid: uuid(),
            fieldIndex: nextFieldIndex,
            required: [DigitalDocumentFieldSetUp.SIGNATURE, DigitalDocumentFieldSetUp.INITIAL].includes(type),
            signatureTypes,
            defaultParticipant: head(participants),
            placeInformation: {
                width: previousFieldInfo.width,
                height: previousFieldInfo.height,
                top: wrapRef.current.scrollTop,
                left: 0
            }
        }
        switch (type) {
            case DigitalDocumentFieldSetUp.INITIAL:
                newField.initialType = head(INITIAL_TYPES)
                break
            case DigitalDocumentFieldSetUp.TEXT:
                newField.dataType = head(DATA_TYPES)
                newField.defaultDataType = head(DEFAULT_DATA_TYPES)
                break
            case DigitalDocumentFieldSetUp.CHECKBOX:
                newField.checkboxType = head(CHECKBOX_TYPES)
                break
            case DigitalDocumentFieldSetUp.GRADE:
                newField.dataType = head(GRADE_DATA_TYPES)
                break
            case DigitalDocumentFieldSetUp.FILE:
                newField.fileType = head(CHECKBOX_TYPES)
                break
            default:
                break
        }
        if (
            [
                DigitalDocumentFieldSetUp.INITIAL,
                DigitalDocumentFieldSetUp.CHECKBOX,
                DigitalDocumentFieldSetUp.SELECT
            ].includes(type)
        ) {
            newField.fieldValues = [
                {
                    id: uuid(),
                    type,
                    parentId: newField.id,
                    parentSignatureTypeId: get(newField, "signatureTypes[0].id", null),
                    label: "",
                    index: 1,
                    placeInformation: {...newField.placeInformation}
                }
            ]
        }
        fields.push(newField)
        dispatch({fields, activeField: newField, fieldBodyContent: newField})
    }

    const onAddNewFieldValue = (parentId, fieldValue, newFieldsData = fields) => {
        if (fieldValue.type === DigitalDocumentFieldSetUp.DATE_FIELD) {
            newFieldsData.push(fieldValue)
            const fieldBodyContent = newFieldsData.find((item) => item.id === parentId)
            dispatch({fields: newFieldsData, fieldBodyContent})
        } else {
            const newFields = newFieldsData.map((field) => {
                if (field.id === parentId) {
                    field.fieldValues.push(fieldValue)
                    return {...field}
                }
                return field
            })
            if (fieldValue.type === DigitalDocumentFieldSetUp.SELECT) {
                dispatch({fields: newFields})
            } else {
                newFields.push(fieldValue)
                const fieldBodyContent = newFields.find((item) => item.id === parentId)
                dispatch({fields: newFields, fieldBodyContent})
            }
        }
    }

    const onRemoveFieldValue = (parentId, id, newFieldsData = fields) => {
        let newFields = []
        if (id === DigitalDocumentFieldSetUp.DATE_FIELD) {
            newFields = newFieldsData.filter((field) => {
                return !(field.parentId === parentId && field.type === DigitalDocumentFieldSetUp.DATE_FIELD)
            })
        } else {
            newFields = newFieldsData
                .map((field) => {
                    if (field.id === parentId) {
                        field.fieldValues = field.fieldValues.filter((fieldValue) => {
                            return fieldValue.id !== id
                        })
                        field.fieldValues.forEach((fieldValue, index) => {
                            fieldValue.index = index + 1
                        })
                        return {...field}
                    }
                    if (!isEmpty(field.dependencies) && field.dependencies.some((item) => item.value?.id === id)) {
                        field.isConditionalRule = false
                        field.dependencies = []
                    }
                    return field
                })
                .filter((field) => {
                    if (field.parentId === parentId && field.id === id) {
                        return false
                    }
                    return true
                })
        }
        const fieldBodyContent = newFields.find((item) => item.id === parentId)
        dispatch({fields: newFields, fieldBodyContent})
    }

    const onClickDelete = (deletedField) => {
        const newFields = fields
            .filter((field) => {
                if (!field.parentId) {
                    return field.id !== deletedField.id
                }
                return field.parentId !== deletedField.id
            })
            .map((field) => {
                if (
                    field.isConditionalRule &&
                    field.dependencies.some((dependencyField) => dependencyField?.uuid?.uuid === deletedField.uuid)
                ) {
                    field.isConditionalRule = false
                    field.dependencies = []
                }
                return field
            })
        dispatch({fields: newFields, activeField: null})
    }

    const onClickField = (field) => {
        const newPreviousFieldInfo = {
            ...previousFieldInfo,
            width: field.placeInformation.width,
            height: field.placeInformation.height
        }
        if (field.parentId) {
            const fieldBodyContent = fields.find((item) => item.id === field.parentId)
            dispatch({fieldBodyContent, previousFieldInfo: newPreviousFieldInfo})
        } else {
            dispatch({fieldBodyContent: field, previousFieldInfo: newPreviousFieldInfo})
        }
    }

    const validateData = () => {
        const fieldsValidator = fields
            .filter((field) => !field.parentId)
            .map((field) => ({
                id: field.id,
                signerIds: (field.signatureTypes || []).map((signatureType) => signatureType.id)
            }))
        const invalidSignerFieldIds = fieldsValidator
            .filter((field) => isEmpty(field.signerIds))
            .map((field) => field.id)
        if (!isEmpty(invalidSignerFieldIds)) {
            setErrorFieldIds(invalidSignerFieldIds)
            toastError("Please select at least 1 signer")
            return true
        }
        const invalidTextFieldIds = fields
            .filter(
                (field) =>
                    field.type === DigitalDocumentFieldSetUp.TEXT &&
                    field.defaultDataType?.id === DefaultDataType.STUDENT_DATA &&
                    (!field.defaultParticipant || !field.defaultData)
            )
            .map((field) => field.id)
        if (!isEmpty(invalidTextFieldIds)) {
            setErrorFieldIds(invalidTextFieldIds)
            toastError("Please select at participant info in text fields")
            return true
        }
        const invalidSelectFieldIds = fields
            .filter(
                (field) =>
                    field.type === DigitalDocumentFieldSetUp.SELECT &&
                    (isEmpty(field.fieldValues) || field.fieldValues.some((fieldValue) => !fieldValue.option))
            )
            .map((field) => field.id)
        if (!isEmpty(invalidSelectFieldIds)) {
            setErrorFieldIds(invalidSelectFieldIds)
            toastError("Please add at least 1 option and label not empty in select fields")
            return true
        }
        const duplicateOptionSelectFieldIds = fields
            .filter(
                (field) =>
                    field.type === DigitalDocumentFieldSetUp.SELECT &&
                    uniq(field.fieldValues.map((fieldValue) => trim(fieldValue.option))).length !==
                        field.fieldValues.length
            )
            .map((field) => field.id)
        if (!isEmpty(duplicateOptionSelectFieldIds)) {
            setErrorFieldIds(duplicateOptionSelectFieldIds)
            toastError("Options in select field must be different")
            return true
        }
        const invalidConditionalFields = fields
            .filter((field) => {
                if (field.isConditionalRule) {
                    const missingValue = field.dependencies.some((item) => !item.condition || !item.uuid)
                    if (missingValue) {
                        return true
                    }
                    const missingEqualValue = field.dependencies.some(
                        (item) => item.condition.id === DigitalTaskCondition.EQUALS_TO && !item.value
                    )
                    if (missingEqualValue) {
                        return true
                    }
                }
                return false
            })
            .map((field) => field.id)
        if (!isEmpty(invalidConditionalFields)) {
            setErrorFieldIds(invalidConditionalFields)
            toastError("Please select condition rule info")
            return true
        }
        const circularFields = getCircularDependencyFields(fields)
        if (!isEmpty(circularFields)) {
            setErrorFieldIds(circularFields.map((item) => item.id))
            toastError("Circular dependency found")
            return true
        }

        setErrorFieldIds(null)
        return false
    }

    const onClickSave = async () => {
        const isInvalid = validateData()
        if (isInvalid) {
            return
        }
        try {
            dispatch({isSaving: true})
            const dataSubmit = getDataSubmit({fields, taskData, margin, pageRef, participants})
            await taskService.editDigitalDocumentField(dataSubmit)
            if (taskData.subtype === TaskSubtype.LIBRARY && queryParams.id) {
                toastSuccess("Task updated successfully")
                const params = new URLSearchParams(location.search)
                params.delete("configure")
                const newSearch = params.toString()
                const newPath = `${location.pathname}${newSearch ? `?${newSearch}` : ""}`
                history.replace(newPath)
                onCloseDigitalDocSetup()
            } else {
                toastSuccess("Task submitted successfully")
                history.push(`/tasks?tab=${getBackUrlTab({type: taskData.type, subtype: taskData.subtype})}`)
            }
        } catch (error) {
            dispatch({isSaving: false})
            handleError(error)
        } finally {
            dispatch({isSaving: false})
        }
    }

    const onChangeCombinedColor = (sortedSignerIds: string, color: string) => {
        const newCombinedColor = {...combinedColor}
        newCombinedColor[sortedSignerIds] = color
        dispatch({combinedColor: newCombinedColor})
    }

    const onDuplicateField = (field) => {
        const {newFields, newField} = duplicateField(fields, field)
        dispatch({fields: newFields, activeField: newField, fieldBodyContent: newField})
    }

    const boundaryWidth = get(bodyRef.current, "offsetWidth", 300) - 32
    const boundaryHeight = get(bodyRef.current, "offsetHeight", 1000) - 32
    const isDraftTask = taskData.isDraft || taskData.status === TaskStatus.DRAFT
    const isEditable =
        !Boolean(taskData.deletedAt) &&
        (isDraftTask ||
            isCreation ||
            ([TaskType.DIGITAL_DOCUMENT, TaskType.CUSTOM_DD].includes(taskData.type) &&
                taskData.subtype === TaskSubtype.LIBRARY))

    if (isEmpty(task)) {
        return <BaseLoading isShow />
    }

    return (
        <div ref={wrapRef} className={styles.wrap}>
            {!isImgsLoaded && <BaseLoading isShow />}
            <div className={styles.headerContainer}>
                <div style={{height: 12}}></div>
                <div className={styles.headerContentWrap}>
                    <div className={styles.headerWrap}>
                        <DefinedUser definedUserTypes={definedUserTypes} />
                        <HeaderBody
                            type={taskData.type}
                            taskId={taskData.id}
                            isDeleted={!!taskData.deletedAt}
                            activeField={activeField}
                            isSaving={isSaving}
                            isEditable={isEditable}
                            onAddField={onAddField}
                            getIconByType={getIconByType}
                            onClickSave={onClickSave}
                        />
                    </div>
                    <CreatedInfo data={taskData} userInfoKey="createdByUser" />
                </div>
            </div>
            <div ref={bodyRef} className={styles.body} style={{width: pageRef?.current?.offsetWidth}}>
                {fields.map((field) => (
                    <FieldAction
                        key={field.id}
                        field={field}
                        fields={fields}
                        fieldBodyContent={fieldBodyContent}
                        activeField={activeField}
                        boundaryWidth={boundaryWidth}
                        boundaryHeight={boundaryHeight}
                        definedUsers={definedUsers}
                        participants={participants}
                        combinedColor={combinedColor}
                        scaleInfo={scaleInfo}
                        totalPage={task.digitalDocument.pages.length}
                        previousFieldInfo={previousFieldInfo}
                        isEditable={isEditable}
                        errorFieldIds={errorFieldIds}
                        onDragStart={onDragStart}
                        onDragStop={onDragStop}
                        onResize={onResize}
                        getIconByType={getIconByType}
                        onClickDelete={onClickDelete}
                        setActiveField={setActiveField}
                        onChangeField={onChangeField}
                        onAddNewFieldValue={onAddNewFieldValue}
                        onRemoveFieldValue={onRemoveFieldValue}
                        onClickField={onClickField}
                        onChangeFieldValue={onChangeFieldValue}
                        onChangeCombinedColor={onChangeCombinedColor}
                        onDuplicateField={onDuplicateField}
                    />
                ))}
                <div className={styles.pageWrap}>
                    {(task.digitalDocument.pages || []).map((page, index) => (
                        <img
                            ref={pageRef}
                            key={index}
                            className={cx(styles.pageImg, {
                                [styles.lastPage]: index === task.digitalDocument.pages.length - 1
                            })}
                            src={page.url}
                            alt=""
                        />
                    ))}
                </div>
            </div>
        </div>
    )
}
